<template>
  <!-- ส่งเข้าจัดรถ: one click replaces keying a planned trip into managecar.
       Check SAP -> trip number -> lock -> UpdateSo / UpdateRT per order ->
       rows + timelines -> done. A partial send is resumed with ลองอีกครั้ง
       (retry mode: the trip is already `sent`, only orders with no row go). -->
  <v-dialog :value="value" max-width="800" scrollable persistent @input="$emit('input', $event)">
    <v-card v-if="trip">
      <v-card-title>{{ title }} · {{ trip.startDate }} → {{ trip.returnDate }}</v-card-title>
      <v-card-text>
        <div class="mb-2">รถ <strong>{{ trip.regNum }}</strong> · วันโหลด {{ loadDate }} · ออกรถ {{ trip.startDate }} · ส่งได้ถึง {{ trip.returnDate }}</div>
        <div v-if="retry" class="mb-2">เลขที่ใบจัดรถ <strong>{{ trip.transNo }}</strong> · คนขับ {{ trip.driver }}</div>
        <v-autocomplete v-else v-model="driver" :items="drivers" :loading="loadingDrivers" label="คนขับ" dense outlined />
        <v-simple-table dense>
          <thead><tr><th class="text-no-wrap">วัน</th><th class="text-no-wrap">จุด</th><th class="text-no-wrap">เวลา</th><th>เลขที่</th><th>ลูกค้า</th><th></th></tr></thead>
          <tbody>
            <tr v-for="r in rows" :key="r.order.id">
              <td class="text-no-wrap">{{ r.planDate }}</td><td>{{ r.planSeq }}</td><td class="text-no-wrap">{{ r.serviceTime }}</td>
              <td>{{ U.handoverRef(r.order) }}</td><td>{{ r.order.name }}</td>
              <td><v-icon v-if="sent[r.order.id]" small color="green">mdi-check</v-icon></td>
            </tr>
          </tbody>
        </v-simple-table>
        <v-alert v-for="(p, i) in problems" :key="'p' + i" dense type="error" class="mt-2 mb-0">{{ p }}</v-alert>
        <v-alert v-for="(w, i) in warnings" :key="'w' + i" dense type="warning" class="mt-2 mb-0">{{ w }}</v-alert>
        <div v-if="step" class="caption mt-2">{{ step }}</div>
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn text :disabled="busy" @click="$emit('input', false)">ปิด</v-btn>
        <span v-if="retry && !planCurrent" class="caption mr-2">บันทึกแผนก่อน</span>
        <v-btn color="primary" :loading="busy" :disabled="retry ? !planCurrent : !driver" @click="go">{{ retry ? 'ลองอีกครั้ง' : 'ส่ง' }}</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import U from '@/components/upcountry'
import P from '@/components/upcountryPlan'
import US from '@/components/upcountryService'
import DR from '@/components/deliveryRow'
import RJ from '@/components/returnJob'
import sapJob from '@/components/sapJob'
import { reportError } from '@/components/report'
// SAP cancels a driver by renaming the row; the prefixes functions/driverRoster.js hides
const HIDDEN_DRIVER = ['ยกเลิก', 'ไม่ใช้', 'ลูกค้ามารับเอง']

export default {
  name: 'upcountrySendDialog',
  // before: the plan the sheet was last written with, when this send follows a
  // re-plan of a sent trip (taken before the save); else trip.sheetPlan is used.
  props: { value: Boolean, trip: { type: Object, default: null }, orders: { type: Array, default: () => [] }, user: { type: String, default: '' }, before: { type: Object, default: null } },
  data () { return { drivers: [], loadingDrivers: false, driver: '', busy: false, step: '', problems: [], warnings: [], sent: {} } },
  computed: {
    U: () => U,
    webPath () { return this.$store.getters.webPath },
    retry () { return !!(this.trip && this.trip.status === 'sent') },
    loadDate () { return this.trip ? U.loadingDay(this.trip.startDate) : '' },
    planCurrent () { return P.planIsCurrent(this.trip) },
    // A sent trip with an order not on the sheet yet: the orders added since.
    title () {
      if (!this.retry) return 'ส่งเข้าจัดรถ'
      return this.orders.some(o => !this.sent[o.id]) ? `ส่งเพิ่มเข้าใบจัดรถ ${this.trip.transNo}` : 'ส่งเข้าจัดรถต่อ'
    },
    plan () { return (this.trip && P.sendPlan(this.trip)) || {} },
    rows () {
      return this.orders.map(o => Object.assign({ order: o }, this.plan[o.id] || {}))
        .sort((a, b) => (a.planSeq || 1e9) - (b.planSeq || 1e9))
    }
  },
  watch: {
    async value (open) {
      if (!open) return
      this.problems = []; this.warnings = []; this.step = ''; this.sent = {}; this.driver = ''
      if (this.retry) await this.markSentFor(this.trip)
      else this.loadDrivers()
    }
  },
  methods: {
    async loadDrivers () {
      if (this.drivers.length) return
      this.loadingDrivers = true
      try { this.drivers = (await sapJob.drivers(this.webPath)).filter(d => !HIDDEN_DRIVER.some(p => String(d.split(' - ').slice(1).join(' - ')).replace(/\s+/g, '').indexOf(p) === 0)) } catch (e) { reportError('GetDriver', e); this.problems = ['อ่านรายชื่อคนขับจาก SAP ไม่ได้'] }
      this.loadingDrivers = false
    },
    // Every SAP read for one order, errors kept per order so checkSend names them all.
    async readSap (o) {
      const out = {}
      try {
        const kind = RJ.jobTypeOf(o)
        if (kind !== 'return') {
          out.so = await sapJob.readSo(this.webPath, o.soNo)
          if (out.so) {
            out.products = await sapJob.readProducts(this.webPath, o.soNo)
            out.payment = await sapJob.readPayment(this.webPath, out.so.typePayment)
          }
        }
        if (o.rtNo) {
          try {
            const r = await sapJob.readRt(this.webPath, o.rtNo)
            if (r) { out.rt = r.rt; out.rtRows = r.rows }
          } catch (e) { if (kind === 'return') throw e }
          if (kind === 'return' && out.rt) out.payment = await sapJob.readPayment(this.webPath, out.rt.typePayment)
        }
      } catch (e) {
        reportError('up-country send: SAP read ' + U.handoverRef(o), e)
        out.error = 'อ่านข้อมูลจาก SAP ไม่ได้'
      }
      return out
    },
    async go () {
      this.busy = true; this.problems = []; this.warnings = []
      try {
        const todo = this.orders.filter(o => !this.sent[o.id])
        this.step = 'ตรวจสอบกับ SAP…'
        const sap = {}
        for (const o of todo) sap[o.id] = await this.readSap(o)
        const check = P.checkSend({ trip: this.trip, orders: todo, sap, retry: this.retry })
        this.warnings = check.warnings.map(w => w.text)
        if (!check.ok) {
          this.problems = check.problems.length ? check.problems.map(p => p.text) : [U.actionMessage({ reason: check.reason })]
          return
        }
        let trip = this.trip
        if (!this.retry) {
          this.step = 'ขอเลขที่ใบจัดรถ…'
          const transNo = await US.freeTransNo(await sapJob.lastTransNo(this.webPath))
          await US.lockSend(trip.id, { transNo, driver: this.driver, user: this.user })
          trip = Object.assign({}, trip, { status: 'sent', transNo, driver: this.driver })
        }
        const sheet = US.sendSheet(trip)
        const driverCode = String(trip.driver).split(' - ')[0]
        const jobs = []
        const failed = []
        for (const o of todo) {
          const s = sap[o.id]
          const p = this.plan[o.id] || {}
          this.step = 'อัปเดต SAP ' + U.handoverRef(o) + '…'
          const payload = docEntry => ({ docEntry, transNo: sheet.transNo, loadDate: sheet.loadDate, delDate: sheet.deliveryDate, driver: driverCode, regNum: sheet.regNum, serviceTime: p.serviceTime || null })
          const rtUsable = s.rt && (!RJ.rtProblem(s.rt) || (RJ.rtProblem(s.rt) === 'DISPATCHED' && String(s.rt.transNo) === sheet.transNo))
          try {
            if (s.so) await sapJob.updateSo(this.webPath, payload(s.products[0].docEntry))
            else await sapJob.updateRt(this.webPath, payload(s.rt.docEntry))
          } catch (e) {
            reportError('up-country send: ' + U.handoverRef(o), e)
            failed.push(U.handoverRef(o) + ': อัปเดต SAP ไม่สำเร็จ')
            continue
          }
          // An exchange's RT failing after its SO went is a note, as on คิวรถเล็ก:
          // the SO already carries the number, so the row is written anyway.
          if (s.so && rtUsable) {
            try { await sapJob.updateRt(this.webPath, payload(s.rt.docEntry)) } catch (e) {
              reportError('up-country send: RT after SO ' + o.rtNo, e)
              this.warnings.push(U.handoverRef(o) + ': อัปเดต RT ใน SAP ไม่สำเร็จ โปรดแก้ไขใน SAP')
            }
          }
          jobs.push(Object.assign(DR.jobFromSap({
            order: o, so: s.so, products: s.products, payment: s.payment,
            rt: rtUsable ? s.rt : null, rtRows: rtUsable ? s.rtRows : null, time: p.serviceTime
          }), { planDate: p.planDate, planSeq: p.planSeq }))
        }
        this.step = 'บันทึกรายการจัดส่ง…'
        if (jobs.length) await US.writeSendRows(trip.id, { sheet, jobs })
        // A re-plan of a sent trip moves rows already on the sheet: their day,
        // stop and window, and SAP's serviceTime where the window changed.
        // Idempotent: a partial send's next ลองอีกครั้ง computes the same changes
        // again (sheetPlan is only written by finishSend) and repeats them,
        // SAP calls included, until a pass finishes.
        if (this.retry) {
          this.step = 'ปรับรายการในใบจัดรถตามแผน…'
          const onSheet = (await US.sendRowsOf(sheet.transNo)).filter(r => US.onSheet(r, trip, trip.id))
          const changes = trip.plan
            ? P.sheetChanges({ before: this.before || trip.sheetPlan || null, after: trip.plan, rows: onSheet })
            : []
          await US.updateSheetRows(trip.id, changes)
          for (const c of changes.filter(x => x.timeChanged)) {
            const o = this.orders.find(x => x.id === c.sourceSlotId)
            const r = onSheet.find(x => x.id === c.rowId) || {}
            const ref = o ? U.handoverRef(o) : (r.soNo || r.rtNo || c.sourceSlotId)
            try {
              const payload = docEntry => ({ docEntry, transNo: sheet.transNo, loadDate: sheet.loadDate, delDate: sheet.deliveryDate, driver: driverCode, regNum: sheet.regNum, serviceTime: c.patch.time || null })
              if (r.status === 'RT') {
                const got = await sapJob.readRt(this.webPath, r.rtNo)
                if (!got) throw new Error('RT not found')
                await sapJob.updateRt(this.webPath, payload(got.rt.docEntry))
              } else {
                const products = await sapJob.readProducts(this.webPath, r.soNo)
                await sapJob.updateSo(this.webPath, payload(products[0].docEntry))
                // An exchange's RT carries the window too, after its SO, as the send does.
                if (r.rtNo && r.rtDocEntry) {
                  try { await sapJob.updateRt(this.webPath, payload(r.rtDocEntry)) } catch (e) {
                    reportError('up-country send: SAP time RT ' + r.rtNo, e)
                    this.warnings.push(`${ref}: อัปเดตเวลา RT ${r.rtNo} ใน SAP ไม่สำเร็จ`)
                  }
                }
              }
            } catch (e) {
              reportError('up-country send: SAP time ' + ref, e)
              this.warnings.push(`${ref}: อัปเดตเวลาใน SAP ไม่สำเร็จ`)
            }
          }
        }
        await this.markSentFor(trip)
        if (failed.length) {
          this.problems = failed.concat(['กด ลองอีกครั้ง เพื่อส่งรายการที่เหลือ'])
          this.$emit('done', { partial: true })
          return
        }
        await US.finishSend(trip.id, { sheetPlan: trip.plan })
        this.step = 'ส่งเข้าจัดรถแล้ว · เลขที่ใบจัดรถ ' + sheet.transNo
        this.$emit('done', { partial: false, transNo: sheet.transNo })
      } catch (e) {
        reportError('up-country send', e)
        this.problems = [U.actionMessage(e)]
      } finally {
        this.busy = false
      }
    },
    async markSentFor (trip) {
      const rows = await US.sendRowsOf(trip.transNo)
      const s = {}
      rows.forEach(r => { if (r.sourceSlotId) s[r.sourceSlotId] = true })
      this.sent = s
    }
  }
}
</script>
